import { parseCollectData, parseAssignmentsData, parseRevisitData } from 'utils/custom';
import * as TYPES from './Collect.types';

const initialState = {
  campaigns: {},
  assignments: {},
  revisit: {},
  /* Latest request id per paginated list, so a response that is no longer current can be dropped
     (see the note on nextRequestId in Collect.actions). */
  listRequestIds: {},
  /* Total revisits, sourced on its own so the sidebar badge does not need the list to be loaded. */
  revisitCount: 0,
  offlineInfo: null,
  collectCount: 0,
  offlineAssignments: [],
  unsyncedSurveys: [],
};

/* True when this success answers the most recent request for that list. The middleware puts the
   same requestId on the request action and on its success, so the pending branch records it and the
   success branch compares. Guards against an older, slower response overwriting a newer one --
   two quick sort clicks used to leave the table showing the ordering it had before them. */
const isCurrent = (state, key, action) => state.listRequestIds?.[key] === action.requestId;

const markRequest = (state, key, action) => ({
  ...state,
  listRequestIds: { ...state.listRequestIds, [key]: action.requestId },
});

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case TYPES.GET_ASSIGNMENT_LIST:
      return markRequest(state, 'assignments', action);
    case TYPES.GET_REVISITS_LIST:
      return markRequest(state, 'revisit', action);
    case TYPES.GET_CAMPAIGN_LIST_SUCCESS: {
      const { data, included, meta } = action?.result;
      const newData = parseCollectData({ data, included });
      return {
        ...state,
        campaigns: {
          data: newData,
          pagination: meta?.pagination,
        },
      };
    }
    case TYPES.GET_ASSIGNMENT_LIST_SUCCESS: {
      if (!isCurrent(state, 'assignments', action)) return state;
      const { data, included, meta } = action?.result;
      const newData = parseAssignmentsData({ data, included });
      return {
        ...state,
        assignments: { data: newData, pagination: meta?.pagination, raw: { data, included } },
      };
    }
    case TYPES.GET_REVISITS_LIST_SUCCESS: {
      if (!isCurrent(state, 'revisit', action)) return state;
      const { data, meta } = action?.result;
      const newData = parseRevisitData({ data }, action?.timeZone);
      return {
        ...state,
        revisit: { data: newData, pagination: meta?.pagination },
        // Keep the badge in step once the tab has loaded.
        revisitCount: meta?.pagination?.count ?? state.revisitCount,
      };
    }
    case TYPES.GET_REVISITS_COUNT_SUCCESS: {
      // Count only: never touches `revisit`, so it cannot flash a row into the table or replace a
      // list response that is still in flight.
      return { ...state, revisitCount: action?.result?.meta?.pagination?.count || 0 };
    }
    case TYPES.GET_ALL_ASSIGNMENTS_LIST_SUCCESS: {
      const { data, included } = action?.result;
      return {
        ...state,
        offlineInfo: {
          assignments: data,
          additionalData: included,
        },
      };
    }
    case TYPES.SET_COLLECT_COUNT: {
      return {
        ...state,
        collectCount: action.payload,
      };
    }
    case TYPES.LOAD_OFFLINE_ASSIGNMENTS_SUCCESS: {
      return {
        ...state,
        offlineAssignments: action.payload,
      };
    }
    case TYPES.ADD_ASSIGNMENTS_TO_OFFLINE_SUCCESS: {
      const incoming = action.payload || [];
      const existingIds = new Set(state.offlineAssignments.map((item) => item.id));
      const merged = [...state.offlineAssignments, ...incoming.filter((item) => !existingIds.has(item.id))];
      return {
        ...state,
        offlineAssignments: merged,
      };
    }
    case TYPES.REMOVE_OFFLINE_ASSIGNMENT_SUCCESS: {
      return {
        ...state,
        offlineAssignments: state.offlineAssignments.filter((item) => item.id !== action.payload),
      };
    }
    case TYPES.CLEAR_OFFLINE_ASSIGNMENTS: {
      return {
        ...state,
        offlineAssignments: [],
        unsyncedSurveys: [],
      };
    }
    case TYPES.LOAD_UNSYNCED_SURVEYS_SUCCESS: {
      return {
        ...state,
        unsyncedSurveys: action.payload,
      };
    }
    case TYPES.REMOVE_UNSYNCED_SURVEY_SUCCESS: {
      return {
        ...state,
        unsyncedSurveys: state.unsyncedSurveys.filter((item) => item.text !== action.payload),
      };
    }
    default:
      return state;
  }
};

export default reducer;
